import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCloudStorage } from '../../hooks/useCloudStorage'
import { useTheme } from '../../contexts/ThemeContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { CheckCircle, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// Polyfill for atob if not available (for JWT decoding)
const atob = (str) => {
  try {
    return window.atob(str)
  } catch (e) {
    return Buffer.from(str, 'base64').toString('binary')
  }
}

/**
 * Google OAuth Callback Page
 * 
 * Handles the callback from Google OAuth and exchanges the authorization code for tokens
 */
const GoogleCallbackPage = () => {
  const navigate = useNavigate()
  const { currentTheme, setTheme } = useTheme()
  const [status, setStatus] = useState('loading')
  const [message, setMessage] = useState('Connecting to Google Drive...')
  const { handleOAuthSuccess } = useCloudStorage()
  
  // Prevent multiple executions of the OAuth callback
  const hasProcessedCallback = useRef(false)

  // Force light mode for OAuth callback page - captive portal should never use dark mode
  useEffect(() => {
    const previousTheme = currentTheme
    if (currentTheme !== 'light') {
      setTheme('light')
    }
    return () => {
      // Restore previous theme when leaving the page
      if (previousTheme !== 'light') {
        setTheme(previousTheme)
      }
    }
  }, [])
  
  const handleCallback = async () => {
    // Prevent multiple executions
    if (hasProcessedCallback.current) {
      console.log('🔒 GoogleCallback: Callback already processed, skipping...')
      return
    }
    hasProcessedCallback.current = true

    try {
      setStatus('loading')
      setMessage('Exchanging authorization code...')
      
      // Get the authorization code from URL
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const oauthState = urlParams.get('state') // OAuth state parameter
      const error = urlParams.get('error')
      
      if (error) {
        throw new Error(`OAuth error: ${error}`)
      }
      
      if (!code) {
        throw new Error('No authorization code received')
      }
      
      console.log('🔄 GoogleCallback: Received authorization code, exchanging for tokens...')
      console.log('🔍 GoogleCallback: OAuth state parameter:', oauthState)
      console.log('🔍 GoogleCallback: OAuth state type:', typeof oauthState)
      console.log('🔍 GoogleCallback: OAuth state length:', oauthState?.length)
      console.log('🔍 GoogleCallback: Raw URL search params:', window.location.search)
      console.log('🔍 GoogleCallback: Full URL:', window.location.href)
      
      // Get user info from OAuth state parameter instead of localStorage
      let userEmail = null
      let stateData = null
      
      if (oauthState && oauthState !== '/notes' && oauthState !== '/onboarding') {
        console.log('🔍 GoogleCallback: Attempting to parse OAuth state...')
        console.log('🔍 GoogleCallback: Raw oauthState:', oauthState)
        console.log('🔍 GoogleCallback: oauthState length:', oauthState.length)
        
        try {
          // Try to decode the state parameter
          const decodedState = decodeURIComponent(oauthState)
          console.log('🔍 GoogleCallback: Decoded state:', decodedState)
          console.log('🔍 GoogleCallback: Decoded state length:', decodedState.length)
          
          stateData = JSON.parse(decodedState)
          console.log('🔍 GoogleCallback: Parsed state data:', stateData)
          console.log('🔍 GoogleCallback: State data keys:', Object.keys(stateData))
          
          userEmail = stateData.userEmail
          console.log('🔍 GoogleCallback: Extracted userEmail:', userEmail)
          
          if (!userEmail) {
            throw new Error('User email not found in OAuth state')
          }
          
          // Validate that the state is not too old (within 10 minutes)
          const stateAge = Date.now() - stateData.timestamp
          console.log('🔍 GoogleCallback: State age (ms):', stateAge)
          if (stateAge > 10 * 60 * 1000) { // 10 minutes
            throw new Error('OAuth state is too old. Please try connecting again.')
          }
          
          console.log('✅ GoogleCallback: User info extracted from OAuth state:', userEmail)
        } catch (parseError) {
          console.error('❌ GoogleCallback: Failed to parse OAuth state:', parseError)
          console.log('⚠️ GoogleCallback: OAuth state parsing failed, falling back to localStorage')
          console.log('⚠️ GoogleCallback: Parse error details:', parseError.message)
          console.log('⚠️ GoogleCallback: Parse error stack:', parseError.stack)
          // Continue to fallback instead of failing immediately
        }
      } else {
        console.log('🔍 GoogleCallback: OAuth state is missing or invalid path:', oauthState)
      }
      
      // If OAuth state parsing failed or state is missing, try localStorage fallback
      if (!userEmail) {
        console.log('🔄 GoogleCallback: Using localStorage fallback for user info')
        console.log('🔍 GoogleCallback: Checking localStorage contents...')
        console.log('🔍 GoogleCallback: authToken exists:', !!localStorage.getItem('authToken'))
        console.log('🔍 GoogleCallback: user exists:', !!localStorage.getItem('user'))
        console.log('🔍 GoogleCallback: user content:', localStorage.getItem('user'))
        
        const currentAuthToken = localStorage.getItem('authToken')
        const currentUser = localStorage.getItem('user')
        
        // Try to decode the JWT token to get user info if user key is missing
        if (!currentUser && currentAuthToken) {
          console.log('🔍 GoogleCallback: User key missing, attempting to decode JWT token...')
          try {
            // JWT tokens have 3 parts separated by dots
            const tokenParts = currentAuthToken.split('.')
            if (tokenParts.length === 3) {
              // Decode the payload (second part)
              const payload = JSON.parse(atob(tokenParts[1]))
              console.log('🔍 GoogleCallback: Decoded JWT payload:', payload)
              
              if (payload.email) {
                console.log('✅ GoogleCallback: Found user email in JWT token:', payload.email)
                userEmail = payload.email
              } else {
                console.warn('⚠️ GoogleCallback: JWT token does not contain email')
              }
            } else {
              console.warn('⚠️ GoogleCallback: JWT token format is invalid')
            }
          } catch (jwtError) {
            console.warn('⚠️ GoogleCallback: Failed to decode JWT token:', jwtError)
          }
        }
        
        // Additional fallback: try to get user email from the current URL or session
        if (!userEmail && !currentAuthToken && !currentUser) {
          console.log('🔍 GoogleCallback: No auth data found, checking for alternative sources...')
          
          // Check if we're coming from a specific page that might have user context
          const referrer = document.referrer
          console.log('🔍 GoogleCallback: Referrer:', referrer)
          
          // If we have no auth data at all, redirect to login
          setStatus('error')
          setMessage('No authentication found. Please log in first.')
          toast.error('No authentication found. Please log in first.')
          
          setTimeout(() => {
            navigate('/login')
          }, 2000)
          return
        }
        
        if (!currentAuthToken || (!currentUser && !userEmail)) {
          console.error('❌ GoogleCallback: No OAuth state and no localStorage auth, cannot proceed')
          console.error('❌ GoogleCallback: authToken:', currentAuthToken ? 'EXISTS' : 'MISSING')
          console.error('❌ GoogleCallback: user:', currentUser ? 'EXISTS' : 'MISSING')
          console.error('❌ GoogleCallback: userEmail from JWT:', userEmail || 'NOT_FOUND')
          
          // Try to get more context about what's happening
          const allKeys = Object.keys(localStorage)
          console.log('🔍 GoogleCallback: All localStorage keys:', allKeys)
          
          setStatus('error')
          setMessage('Authentication information not found. Please try connecting again.')
          toast.error('Authentication information not found. Please try connecting again.')
          
          setTimeout(() => {
            navigate('/onboarding')
          }, 2000)
          return
        }
        
        try {
          const userData = JSON.parse(currentUser)
          userEmail = userData.email
          if (!userEmail) {
            throw new Error('User email not found')
          }
          console.log('✅ GoogleCallback: User info extracted from localStorage fallback:', userEmail)
        } catch (parseError) {
          console.error('❌ GoogleCallback: Failed to parse user data from localStorage:', parseError)
          console.error('❌ GoogleCallback: Raw user data:', currentUser)
          
          // If we have userEmail from JWT, use that instead
          if (userEmail) {
            console.log('✅ GoogleCallback: Using userEmail from JWT token instead')
          } else {
            setStatus('error')
            setMessage('Invalid user data. Please log in again.')
            toast.error('Invalid user data. Please log in again.')
            
            setTimeout(() => {
              navigate('/login')
            }, 2000)
            return
          }
        }
      }
      
      console.log('✅ GoogleCallback: User authenticated, proceeding with OAuth callback for:', userEmail)
      console.log('🔍 GoogleCallback: Request payload:', {
        code: code ? code.substring(0, 20) + '...' : 'null',
        state: oauthState,
        user_id: userEmail,
        redirect_uri: window.location.origin + '/auth/google/callback'
      })
      
      // Exchange authorization code for access token
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code,
          state: oauthState,
          user_id: userEmail,
          redirect_uri: window.location.origin + '/auth/google/callback'
        })
      })
      
      if (!response.ok) {
        let errorData
        try {
          errorData = await response.json()
        } catch (e) {
          errorData = await response.text()
        }
        console.error('OAuth callback failed:', response.status, errorData)
        console.error('Response headers:', Object.fromEntries(response.headers.entries()))
        throw new Error(`Failed to exchange authorization code: ${response.status} - ${JSON.stringify(errorData)}`)
      }
      
      const result = await response.json()
      
      console.log('✅ GoogleCallback: User authentication verified, proceeding with OAuth success handling')
      
      // Update CloudStorageContext with successful connection
      const authResult = await handleOAuthSuccess(result)
      
      if (!authResult.success) {
        throw new Error('Failed to handle OAuth success')
      }
      
      console.log('✅ GoogleCallback: Auth tokens stored successfully')
       
       setStatus('success')
       setMessage('Google Drive connected successfully!')
       toast.success('Google Drive connected!')
       
       // Redirect back to notes page
       setTimeout(() => {
         console.log('🚀 GoogleCallback: Redirecting to notes page')
         navigate('/notes', { replace: true })
       }, 1000)
      
    } catch (error) {
      console.error('Callback error:', error)
      setStatus('error')
      setMessage('Failed to complete connection')
      toast.error('Connection failed')
      
      setTimeout(() => {
        navigate('/onboarding')
      }, 2000)
    }
  }

  useEffect(() => {
    handleCallback()
  }, []) // Empty dependency array to ensure it only runs once

  return (
    <div className="min-h-screen auth-gradient-bg flex items-center justify-center py-6 px-4 sm:py-12">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <LoadingSpinner />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connecting Google Drive
            </h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Success!
            </h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-2">
              Redirecting you back...
            </p>
            {/* Manual continue button in case redirect fails */}
            <button
              onClick={() => navigate('/notes', { replace: true })}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Notes
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connection Failed
            </h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-2">
              Redirecting you back...
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default GoogleCallbackPage 